// RiskCommentary (TASK-582 honest-scope) — the persisted FINMA-style risk read-out the
// generate_portfolio_risk skill writes (measured VaR/vol/beta/concentration/stress checked
// against tolerance, deterministic risk flags, since-last-time delta), rendered as a canvas
// tile: prose + flag chips + delta line. Sibling of PortfolioCommentary. Self-fetches by
// clientId; a miss auto-generates server-side, so it polls while status === "generating".
import { useQuery } from '@tanstack/react-query';
import { portfolioApiService, type RiskFlag, type PortfolioRiskLatest } from '../../../services/api/portfolioApiService';
import { WidgetContainer } from '../WidgetContainer';

const POLL_MS = 5000;
const POLL_CAP_MS = 90_000;

const FLAG_LABEL: Record<string, string> = {
  volatility: 'Volatility',
  value_at_risk: 'VaR',
  concentration: 'Concentration',
  stress: 'Stress',
};

// status → chip tone. "unknown" is a data gap, styled neutral — never green.
const STATUS_CLASS: Record<string, string> = {
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  flag: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  severe: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  unknown: 'bg-accent text-muted-foreground',
};

function FlagChip({ id, flag }: { id: string; flag?: RiskFlag }) {
  if (!flag) return null;
  const tone = STATUS_CLASS[flag.status] ?? STATUS_CLASS.unknown;
  const extra = flag.worst ? ` · ${flag.worst.scenario}` : '';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      {FLAG_LABEL[id]}: {flag.status}{extra}
    </span>
  );
}

export function RiskCommentary({ clientId }: { clientId: string }) {
  const startedAt = Date.now();
  const { data, isLoading, isError } = useQuery<PortfolioRiskLatest>({
    queryKey: ['risk-commentary', clientId],
    queryFn: () => portfolioApiService.getLatestPortfolioRisk(clientId),
    enabled: !!clientId,
    refetchInterval: (query) => {
      const latest = query.state.data;
      const generating = latest?.status === 'generating' && !latest?.analysis;
      return generating && Date.now() - startedAt < POLL_CAP_MS ? POLL_MS : false;
    },
  });

  const analysis = data?.analysis ?? null;
  const flags = analysis?.flags ?? null;
  const deltaChanges = analysis?.delta?.changes ?? [];
  const dq = flags?.data_quality;

  return (
    <WidgetContainer
      title="Risk commentary"
      source="Risk · AI read-out"
      // TASK-825: the analysis's own timestamp.
      dataAsOf={analysis?.generated_at ?? null}
    >
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && (
        <div className="text-[13px] text-muted-foreground">Could not load the risk read-out.</div>
      )}
      {!isLoading && !isError && !analysis && data?.status === 'generating' && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-transparent" />
          Generating the risk read-out from measured figures…
        </div>
      )}
      {!isLoading && !isError && !analysis && data?.status !== 'generating' && (
        <div className="text-[13px] text-muted-foreground">
          No risk read-out available for this client — no portfolio risk data was found.
        </div>
      )}
      {analysis && (
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {(['volatility', 'value_at_risk', 'concentration', 'stress'] as const).map((k) => (
              <FlagChip key={k} id={k} flag={flags?.[k]} />
            ))}
            {typeof analysis.confidence_score === 'number' && (
              <span className="ml-auto rounded-full bg-accent px-2 py-0.5 text-[12px] font-medium text-foreground">
                {Math.round(analysis.confidence_score * 100)}% confidence
              </span>
            )}
          </div>

          {dq && dq.sufficient === false && (
            <div className="mb-3 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
              Market-risk metrics await more history ({dq.returns_count} day(s) on file). Concentration and stress figures remain valid.
            </div>
          )}
          {dq && dq.sufficient !== false && dq.source === 'synthetic' && (
            <div className="mb-3 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
              Based on simulated history — figures are illustrative.
            </div>
          )}

          <p className="whitespace-pre-line text-[13px] text-muted-foreground">
            {analysis.risk_analysis}
          </p>

          <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            {deltaChanges.length > 0 ? (
              <span>Since last read-out: {deltaChanges.join(' · ')}</span>
            ) : analysis.delta ? (
              <span>Since last read-out: no material change</span>
            ) : (
              <span>First risk read-out for this client</span>
            )}
            {analysis.generated_at && ` · ${new Date(analysis.generated_at).toLocaleDateString()}`}
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}
